import type { ItemNode, OrdersParseTree } from "../parser/types";
import type { ConfirmatoryQuestion } from "./types";

/**
 * Paragraph and subparagraph questions, keyed by the canonical ids doc 04
 * section 5.4 fixes. The prompt uses that section's own spoken form, so no
 * wording is invented here.
 */
const SECTION_PROMPTS: ReadonlyMap<string, string> = new Map([
  ["MISSION", "State the mission."],
  ["SITUATION/ENEMY", "State the enemy situation."],
  ["SITUATION/FRIENDLY", "State the friendly situation."],
  ["SITUATION/ATTS", "State the attachments and detachments."],
  ["EXECUTION/GT", "State the groupings and tasks."],
  ["EXECUTION/COORD", "State the coordinating instructions."],
  ["SERVICE", "State the service and support."],
  ["COMDSIGS", "State the command and signals."],
]);

/**
 * Item questions, scoped to the squadron's own plan inside the concept of
 * operations. Each of these labels also appears under SITUATION describing
 * higher's plan, and a question must never resolve to that copy, so the path
 * is fixed here rather than matched on the label alone.
 */
const CONOPS_ITEM_PROMPTS: ReadonlyMap<string, string> = new Map([
  ["Main Effort", "State the main effort."],
  ["End State", "State the end state."],
  ["Scheme of Manoeuvre", "State the scheme of manoeuvre."],
]);

/** The only subparagraph whose items are eligible. Doc 04 section 5.6. */
const CONOPS_PATH = "EXECUTION/CONOPS";

function firstSpokenValue(items: readonly ItemNode[]): string | null {
  for (const item of items) {
    const text = item.displayText.trim();
    if (text !== "") return text;
  }
  return null;
}

function labelOf(item: ItemNode): string {
  return item.label?.trim() ?? "";
}

/**
 * Builds the question bank from the parse tree. A question is created only
 * where the document actually carries the node, so nothing is generated from
 * content and nothing is invented.
 */
export function buildQuestionBank(
  tree: OrdersParseTree,
): readonly ConfirmatoryQuestion[] {
  const questions: ConfirmatoryQuestion[] = [];

  for (const paragraph of tree.paragraphs) {
    const sectionPrompt = SECTION_PROMPTS.get(paragraph.id);
    const answer = firstSpokenValue(paragraph.items);
    if (sectionPrompt !== undefined && answer !== null) {
      questions.push({
        id: `question/${paragraph.id}`,
        nodeId: paragraph.id,
        prompt: sectionPrompt,
        answer,
      });
    }

    for (const subparagraph of paragraph.subparagraphs) {
      const subPrompt = SECTION_PROMPTS.get(subparagraph.id);
      const subAnswer = firstSpokenValue(subparagraph.items);
      if (subPrompt !== undefined && subAnswer !== null) {
        questions.push({
          id: `question/${subparagraph.id}`,
          nodeId: subparagraph.id,
          prompt: subPrompt,
          answer: subAnswer,
        });
      }

      if (subparagraph.id !== CONOPS_PATH) continue;
      for (const item of subparagraph.items) {
        // A labelled item always carries at least its own label, so the
        // prompt lookup is the only guard needed here.
        const itemPrompt = CONOPS_ITEM_PROMPTS.get(labelOf(item));
        const text = item.displayText.trim();
        if (itemPrompt === undefined) continue;
        questions.push({
          id: `question/${item.id}`,
          nodeId: item.id,
          prompt: itemPrompt,
          answer: text,
        });
      }
    }
  }

  return questions;
}
